import { useEffect, useState } from "react";
import { Box, HStack, SimpleGrid, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiChatBubbleLeftRight, HiTableCells, HiTag } from "react-icons/hi2";
import dayjs from "dayjs";
import { LeadService } from "@/services/LeadService";
import { SectionCard, Grid2 } from "@/components/jobOffer/ui";
import InfoField from "../InfoField";
import { extractInteractionData } from "./leadCollectedDataHelpers";
import AnswerValue from "../AnswerValue";

// A plain (non-collapsible) card header -- everything on this tab should be visible at
// once (short, glanceable lead facts), not hidden behind clicks.
const CardHeader = ({ icon: Icon, title }) => (
    <HStack gap={3} mb={4}>
        <Box p={2} bg="purple.50" borderRadius={8} color="purple.500" display="flex">
            <Icon />
        </Box>
        <Text fontWeight={700} color="gray.900" fontSize="sm">{title}</Text>
    </HStack>
);

const LeadDataTab = ({ lead }) => {
    const [interactions, setInteractions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        LeadService.getInteractions(lead.id, 1, 100)
            .then((data) => setInteractions(data?.items ?? []))
            .finally(() => setIsLoading(false));
    }, [lead.id]);

    const additionalEntries = Object.entries(lead.additionalData ?? {});
    const campaignProducts = lead.campaignProducts ?? [];

    const conversationsWithData = interactions
        .map((interaction) => ({ interaction, ...extractInteractionData(interaction.collectedAnswers) }))
        .filter(({ displayAnswers, displayQA, displayDocs }) => displayAnswers.length > 0 || displayQA.length > 0 || displayDocs.length > 0);

    return (
        <VStack gap={4} align="stretch">
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                <SectionCard>
                    <CardHeader icon={HiTag} title="Atrybuty kampanii" />
                    <Grid2>
                        <InfoField label="Data przejęcia">
                            <Text color="gray.900" fontSize="sm">{dayjs(lead.createdAt).format("DD.MM.YYYY HH:mm")}</Text>
                        </InfoField>
                        <InfoField label="Produkty kampanii">
                            {campaignProducts.length > 0 ? (
                                <VStack align="flex-start" gap={1}>
                                    {campaignProducts.map((p) => (
                                        <Text key={p.id} color="gray.900" fontSize="sm">{p.name}</Text>
                                    ))}
                                </VStack>
                            ) : (
                                <Text color="gray.500" fontSize="sm">
                                    {lead.campaignId ? "Kampania nie ma zdefiniowanych produktów" : "Lead nie jest przypisany do kampanii"}
                                </Text>
                            )}
                        </InfoField>
                    </Grid2>
                </SectionCard>

                <SectionCard>
                    <CardHeader icon={HiTableCells} title="Dodatkowe informacje" />
                    {additionalEntries.length > 0 ? (
                        <Grid2>
                            {additionalEntries.map(([key, value]) => (
                                <InfoField key={key} label={key}>
                                    <Text color="gray.900" fontSize="sm">{value}</Text>
                                </InfoField>
                            ))}
                        </Grid2>
                    ) : (
                        <Text color="gray.500" fontSize="sm">Brak dodatkowych informacji</Text>
                    )}
                </SectionCard>
            </SimpleGrid>

            <SectionCard>
                <CardHeader icon={HiChatBubbleLeftRight} title="Zebrane dane" />
                {isLoading ? (
                    <VStack py={6} align="center"><Spinner color="purple.400" size="sm" /></VStack>
                ) : conversationsWithData.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">Brak zebranych danych z rozmów</Text>
                ) : (
                    <VStack align="stretch" gap={3}>
                        {conversationsWithData.map(({ interaction, displayAnswers, displayQA, displayDocs }) => (
                            <Box
                                key={interaction.id}
                                bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius={8} p={4}
                            >
                                <Text color="gray.500" fontSize="xs" mb={3}>
                                    {dayjs(interaction.createdAt).format("DD.MM.YYYY HH:mm")}
                                </Text>

                                {displayAnswers.length > 0 && (
                                    <Box mb={displayQA.length > 0 || displayDocs.length > 0 ? 4 : 0}>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                            {displayAnswers.map((a) => (
                                                <InfoField key={a.name} label={a.display_name}>
                                                    <Text color="gray.900" fontSize="sm">{a.value}</Text>
                                                </InfoField>
                                            ))}
                                        </SimpleGrid>
                                    </Box>
                                )}

                                {displayQA.length > 0 && (
                                    <Box mb={displayDocs.length > 0 ? 4 : 0} bg="white" border="1px solid" borderColor="gray.200" borderRadius={8} overflow="hidden">
                                        {displayQA.map((item, i) => (
                                            <Box
                                                key={item.name} px={4} py={3}
                                                borderBottom={i < displayQA.length - 1 ? "1px solid" : "none"} borderColor="gray.200"
                                            >
                                                <Text color="gray.500" fontSize="sm" mb={1}>{item.name}</Text>
                                                <AnswerValue item={item} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {displayDocs.length > 0 && (
                                    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius={8} overflow="hidden">
                                        {displayDocs.map((item, i) => (
                                            <Box
                                                key={item.name} px={4} py={3}
                                                borderBottom={i < displayDocs.length - 1 ? "1px solid" : "none"} borderColor="gray.200"
                                            >
                                                <Text color="gray.500" fontSize="sm" mb={1}>{item.name}</Text>
                                                <AnswerValue item={item} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </VStack>
                )}
            </SectionCard>
        </VStack>
    );
};

export default LeadDataTab;
